import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Home, Layers, ArrowRight, Phone } from 'lucide-react';
import { useDynamicSEO } from '../hooks/useSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useDynamicSEO({
    title: project ? `${project.title} – ${project.type} in ${project.location} | LIVAG AG` : 'Projekt | LIVAG AG',
    description: project ? `${project.title}: ${project.type} in ${project.location}. ${project.units}. Immobilienentwicklung von LIVAG AG.` : '',
    canonical: project ? `https://www.livag.ch/projekte/${project.id}` : 'https://www.livag.ch/projekte',
    ogImage: project?.image || null
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [projectRes, allRes] = await Promise.all([
          fetch(`${API_URL}/api/projects/${id}`),
          fetch(`${API_URL}/api/projects/public`)
        ]);
        if (projectRes.ok) {
          setProject(await projectRes.json());
        }
        if (allRes.ok) {
          const all = await allRes.json();
          setRelatedProjects(all.filter(p => p.id !== id).slice(0, 3));
        }
      } catch (err) {
        console.error('Error fetching project:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-[#264F77] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#264F77] mb-4">Projekt nicht gefunden</h1>
          <Link to="/projekte" className="text-[#264F77] hover:underline">
            Zurück zur Projektübersicht
          </Link>
        </div>
      </div>
    );
  }

  const gallery = project.gallery?.length > 0 ? project.gallery : [project.image];

  return (
    <div className="min-h-screen" data-testid="project-detail-page">
      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "RealEstateListing",
        "name": project.title,
        "description": project.description,
        "address": { "@type": "PostalAddress", "addressLocality": project.location, "addressCountry": "CH" },
        "image": project.image,
        "url": `https://www.livag.ch/projekte/${project.id}`
      }) }} />

      {/* Back Nav */}
      <div className="pt-24 pb-6 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/projekte" className="inline-flex items-center text-gray-600 hover:text-[#264F77] transition-colors">
            <ArrowLeft className="mr-2" size={20} />
            Zurück zu allen Projekten
          </Link>
        </div>
      </div>

      {/* Hero Image */}
      <section className="relative h-[60vh] overflow-hidden">
        <img
          src={project.image}
          alt={`${project.title} – ${project.type || 'Immobilienprojekt'} in ${project.location} | LIVAG AG`}
          width="1920"
          height="1080"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-7xl mx-auto">
            <span className={`inline-block px-4 py-2 text-white text-sm font-semibold rounded-full mb-4 ${
              project.status === 'In Entwicklung' ? 'bg-blue-500' :
              project.status === 'Realisiert' ? 'bg-green-500' : 'bg-gray-500'
            }`}>
              {project.status}
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-white" data-testid="project-title">
              {project.title}
            </h1>
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-[#264F77] mb-6">Projektübersicht</h2>
              <p className="text-gray-700 leading-relaxed mb-6 text-lg">{project.description}</p>

              {/* Features */}
              {project.features?.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-xl font-semibold text-[#264F77] mb-4">Highlights</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {project.features.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-3 text-sm text-gray-700">
                        <div className="w-2 h-2 bg-[#264F77] rounded-full flex-shrink-0" />
                        {f}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Gallery */}
              {gallery.length > 1 && (
                <div className="mt-12">
                  <h3 className="text-2xl font-bold text-[#264F77] mb-6">Bilder & Visualisierungen</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {gallery.map((image, index) => (
                      <div key={index} className="rounded-lg overflow-hidden shadow-md">
                        <img
                          src={image}
                          alt={`${project.title} – Ansicht ${index + 1}`}
                          loading="lazy"
                          className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div>
              <div className="bg-[#F5F5F5] rounded-lg p-8 sticky top-24">
                <h3 className="text-2xl font-bold text-[#264F77] mb-6">Eckdaten</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-3">
                    <MapPin className="text-[#264F77] flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Standort</p>
                      <p className="font-medium text-gray-800">{project.location}</p>
                      {project.address && <p className="text-sm text-gray-600">{project.address}</p>}
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Layers className="text-[#264F77] flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Status</p>
                      <p className="font-medium text-gray-800">{project.status}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Home className="text-[#264F77] flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Nutzung</p>
                      <p className="font-medium text-gray-800">{project.type}</p>
                    </div>
                  </div>
                  {project.units && (
                    <div className="flex items-start space-x-3">
                      <Layers className="text-[#264F77] flex-shrink-0 mt-1" size={20} />
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Einheiten</p>
                        <p className="font-medium text-gray-800">{project.units}</p>
                      </div>
                    </div>
                  )}
                  {project.year && (
                    <div className="flex items-start space-x-3">
                      <Calendar className="text-[#264F77] flex-shrink-0 mt-1" size={20} />
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Jahr</p>
                        <p className="font-medium text-gray-800">{project.year}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-8 pt-8 border-t border-gray-300 space-y-3">
                  <Link to="/kontakt" className="block w-full text-center px-6 py-3 bg-[#264F77] text-white font-semibold rounded-md hover:bg-[#1e3d5c] transition-colors">
                    Kostenloses Erstgespräch vereinbaren
                  </Link>
                  <a href="tel:0712261199" className="flex items-center justify-center gap-2 w-full text-center px-6 py-3 border border-[#264F77] text-[#264F77] font-semibold rounded-md hover:bg-[#264F77]/5 transition-colors">
                    <Phone size={16} /> 071 226 11 99
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="py-16 bg-[#F5F5F5]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-[#264F77] mb-8">Weitere Projekte</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedProjects.map((rp) => (
                <Link key={rp.id} to={`/projekte/${rp.id}`} className="group block bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img src={rp.image} alt={`${rp.title} – ${rp.location}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#264F77] mb-2">{rp.title}</h3>
                    <p className="text-gray-600 text-sm">{rp.location}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
